import { TextInput } from '@infotech-soe/erp-ui-kit';
import { AuthData, AuthMethod } from '@infotech-soe/merps/types/session';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { AvailableApplication } from '../../../services/auth/types';

import ApplicationSelector from './ApplicationSelector';
import BaseAuthForm from './BaseAuthForm';
import useLoginFormState from './hooks/useLoginFormState';
import LoginFormField from './LoginFormField';
import { loginFormMessages } from './messages';
import { PasswordInput } from './PasswordInput';

interface WindowsADAuthContainerProps {
  hasError: boolean;
  applications?: AvailableApplication[];
  kerberosMode?: boolean;
  onSubmit: (data: AuthData & { application: string | null }) => void;
}

const WindowsADAuthContainer: React.FC<WindowsADAuthContainerProps> = ({
  hasError,
  applications,
  kerberosMode = false,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { values: iState, validation },
    change,
  } = useLoginFormState({
    login: '',
    pass: '',
    pass1: '',
    pass2: '',
    app: '',
  });

  const [isLoginValid, setIsLoginValid] = React.useState(false);

  const handleLoginValidate = useCallback((errors: number) => {
    setIsLoginValid(errors === 0);
  }, []);

  useEffect(() => {
    if (!applications || applications.length === 0) return;
    const isCurrentValid = applications.some(a => a.name === iState.app);
    if (!isCurrentValid) {
      const stored = localStorage.getItem('app');
      const storedApp = applications.find(a => a.name === stored);
      change('app')(storedApp ? storedApp.name : applications[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications]);

  const handleSubmit = useCallback(() => {
    localStorage.setItem('app', iState.app);
    if (kerberosMode) {
      onSubmit({
        type: AuthMethod.KERBEROS,
        application: iState.app || null,
      });
    } else {
      onSubmit({
        type: AuthMethod.WINDOWS_AD,
        userName: iState.login,
        password: iState.pass,
        application: iState.app,
      });
    }
  }, [iState, kerberosMode, onSubmit]);

  const hasApps = (applications?.length ?? 0) > 0;
  const isFormValid = kerberosMode ? hasApps : validation.valid && isLoginValid;

  return (
    <BaseAuthForm
      hasError={hasError}
      logoSrc="/logo-AD.png"
      onSubmit={handleSubmit}
      valid={isFormValid}
      withChange={false}
      withTitle={true}
      textSubtitle={
        kerberosMode ? undefined : loginFormMessages.windowsADSubtitle
      }
    >
      <ApplicationSelector
        applications={applications ?? []}
        value={iState.app}
        onChange={change('app')}
      />
      {!kerberosMode && (
        <>
          <LoginFormField label={formatMessage(loginFormMessages.login)}>
            <TextInput
              autoComplete="username"
              value={iState.login}
              required
              placeholder="username@domain.com"
              regExp={/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/}
              onChange={change('login')}
              onValidate={handleLoginValidate}
            />
          </LoginFormField>
          <PasswordInput
            autoComplete="current-password"
            label={formatMessage(loginFormMessages.password)}
            value={iState.pass}
            required
            onChange={change('pass')}
          />
        </>
      )}
    </BaseAuthForm>
  );
};

export default WindowsADAuthContainer;
